// Ollama Embedding Adapter
// Adapter for Ollama embeddings API

import { createOllamaClient } from '../../client/ollama/index.js'

const DEFAULT_MODEL = 'nomic-embed-text'
const DEFAULT_HOST = 'localhost:11434'

/**
 * Create Ollama embedding adapter
 * @param {Function} httpRequest - HTTP request function
 * @returns {Object} Ollama adapter with embed, isAvailable
 */
export function createOllamaAdapter(httpRequest) {
  const ollamaClient = createOllamaClient(httpRequest)

  /**
   * Generate embeddings using Ollama
   * @param {Object} params
   * @param {string|string[]} params.texts - Text(s) to embed
   * @param {string} params.model - Model name
   * @param {Object} params.config - Provider config
   * @returns {Promise<number[][]>} Array of embedding vectors
   */
  const embed = async ({ texts, model = DEFAULT_MODEL, config = {} }) => {
    const { host = DEFAULT_HOST } = config

    // Normalize to array
    const isString = typeof texts === 'string'
    const inputs = isString ? [texts] : texts

    try {
      // Ollama embeddings API only accepts single text at a time
      // We need to batch process
      const embeddings = []

      for (const text of inputs) {
        const response = await ollamaClient.embeddings({
          model,
          prompt: text,
          host,
        })

        if (!response.embedding || !Array.isArray(response.embedding)) {
          throw new Error('Invalid response from Ollama: missing embedding')
        }

        embeddings.push(response.embedding)
      }

      return embeddings
    } catch (error) {
      throw new Error(`Ollama embedding failed: ${error.message}`)
    }
  }

  /**
   * Check if Ollama is available
   * @param {Object} params
   * @param {Object} params.config - Provider config
   * @returns {Promise<boolean>}
   */
  const isAvailable = async ({ config = {} }) => {
    const { host = DEFAULT_HOST } = config

    try {
      return await ollamaClient.checkAvailability(host)
    } catch (error) {
      return false
    }
  }

  return { embed, isAvailable }
}
